import { PageSectionHeader } from "@components/library/PageSectionHeader";
import { DescriptionDetails, DescriptionList, DescriptionTerm } from "../../DescriptionList";
import { InfoTooltip } from "@components/molecules/InfoTooltip";
import CodePreview from "../../Code/CodePreview";
import { isEmpty, isObject } from "lodash";
import SplitTag from "@components/library/SplitTag";
import { titleFromSnakeOrCamel } from "@/lib/string";
import { StopWordsSplitTag } from "@components/PromptEditor/Parameters/StopWordsInput";
import { ProviderLogo } from "@components/atoms/ProviderLogo";
import { PromptKernelRequest } from "@/types/app/prompt";

type ParametersDetailsProps = Omit<PromptKernelRequest, "linked_tools" | "tools">;

export const ParametersDetails = (props: ParametersDetailsProps) => {
  return (
    // Min h of roughly three lines on param section to stop layout shift as navigating between prompts
    <div className="min-h-[128px]">
      <PageSectionHeader title="Parameters" size="sm" />
      <div className=" inline-flex flex-wrap items-start gap-x-8 gap-y-8">
        {props.provider && (
          <SplitTag
            tagLabel="Model"
            tagValue={
              <>
                <span className="flex items-center gap-4">
                  <ProviderLogo name={props.provider} className={"h-14 w-14 opacity-70"} /> {props.model}
                </span>
              </>
            }
          />
        )}
        {/* Always show these */}
        {(["temperature"] as const).map((key) => {
          if (key in props) {
            let value = props[key];
            return (
              <SplitTag
                key={key}
                tagLabel={titleFromSnakeOrCamel(key)}
                tagValue={isObject(value) ? JSON.stringify(value) : value}
              />
            );
          }
          return null;
        })}
        {/* Only show if non-default value */}
        {(
          [
            ["max_tokens", -1],
            ["top_p", 1],
            ["frequency_penalty", 0],
            ["presence_penalty", 0],
          ] as const
        ).map(([key, defaultValue]) => {
          if (key in props) {
            const value = props[key as keyof ParametersDetailsProps];
            if (value === null || value === undefined || value === defaultValue) return null;
            return (
              <SplitTag
                key={key}
                tagLabel={titleFromSnakeOrCamel(key)}
                tagValue={isObject(value) ? JSON.stringify(value) : value}
              />
            );
          }
          return null;
        })}

        {props.stop && <StopWordsSplitTag value={props.stop} />}

        {/* Only show if has defined value */}
        {(["seed"] as const).map((key) => {
          if (key in props) {
            let value = props[key];
            if (value === null || value === undefined) return null;
            return (
              <SplitTag
                key={key}
                tagLabel={titleFromSnakeOrCamel(key)}
                tagValue={isObject(value) ? JSON.stringify(value) : value}
              />
            );
          }
          return null;
        })}

        {/* Special handling for 'response_format' which is an object but so far only has one key (json mode) so we can just show the value */}
        {props.response_format && (
          <SplitTag tagLabel={"JSON Mode"} tagValue={props.response_format.type === "json_object" ? "On" : "Off"} />
        )}
      </div>
      {!isEmpty(props.other) && (
        <div className="flex flex-col  gap-x-12 py-24">
          <div className="flex items-center gap-4">
            <span className="text-12-14 text-text-base-4"> Other parameters </span>{" "}
            <InfoTooltip content="Additional provider parameters that affect the output" />
          </div>
          <CodePreview language="json" wrapLongLines className="!px-0 text-12-16">
            {JSON.stringify(props.other, null, 2)}
          </CodePreview>
        </div>
      )}
    </div>
  );
};

export const ParameterItem = ({
  label,
  value,
  lowercaseLabel = true,
}: {
  label: string;
  value: any;
  lowercaseLabel?: boolean;
}) => {
  return (
    <div className="flex items-center gap-4">
      <span className="text-10-12 font-medium text-text-base-4">{lowercaseLabel ? label.toLowerCase() : label}</span>
      <span className="font-mono text-10-12 text-text-base-3">{isObject(value) ? JSON.stringify(value) : value}</span>
    </div>
  );
};

/**  Small version of the parameters details for use in the version popover */
export const ParametersDetailsSmall = (props: ParametersDetailsProps) => {
  return (
    <div className=" ">
      <div className="flex flex-wrap items-start gap-x-12">
        {props.provider && <ParameterItem label="model" value={props.model} />}

        {/* Only show if non-default value */}
        {(
          [
            ["temperature", 0],
            ["max_tokens", -1],
            ["top_p", 1],
            ["frequency_penalty", 0],
            ["presence_penalty", 0],
          ] as const
        ).map(([key, defaultValue]) => {
          if (key in props) {
            const value = props[key as keyof ParametersDetailsProps];
            if (value === null || value === undefined || value === defaultValue) return null;
            return (
              <div key={key} className="flex flex-wrap items-center gap-x-12 gap-y-4">
                <ParameterItem key={key} label={titleFromSnakeOrCamel(key)} value={value} />{" "}
              </div>
            );
          }
          return null;
        })}

        {/* Only show if has defined value */}
        {(["seed"] as const).map((key) => {
          if (key in props) {
            let value = props[key];
            if (value === null || value === undefined) return null;
            return (
              <div key={key} className="flex flex-wrap items-center gap-x-12 gap-y-4">
                <ParameterItem key={key} label={titleFromSnakeOrCamel(key)} value={value} />
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};
